import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ColorInput,
  EnhancedSlider,
  GridDropdown,
  GridUploadInput,
  MaterialMapRow,
  MaterialShadowRow,
  ToggleCheckbox,
} from '../../utils/gridUtils';
import { MaterialCommand, MaterialTextureMapCommand } from '../../utils/Command';
import undoRedoManager from '../../utils/UndoRedoManager';
import {
  updateObjeUrl,
  readShadowFromObject,
  applyShadowSettingsToObject,
  buildMaterialSettingsPayload,
  buildMaterialPanelStateForSelection,
  applyAlbedoColorToObject,
  resolveMaterialType,
  sanitizeMaterialPanelState,
  MATERIAL_SIDE_OPTIONS,
  MATERIAL_TYPE_OPTIONS,
} from '../gridpagefunction/materialFunction';
import {
  collectMaterialMapList,
  setMaterialMapVisibility,
  syncMapVisibilitySettings,
  isPublishableTextureUrl,
  setMapPreviewUrl,
  getMeshesFromObject,
  captureObjectMapSlotStates,
  ensureTransparentForActiveAlphaMap,
} from '../../utils/ThreeJsUtils/materialMaps';
import { shouldShowFullMaterialEditor } from '../../utils/ThreeJsUtils/materialPropertyEditor';
import MaterialPresetFields from './MaterialPresetFields';

const MAP_KEY_TO_UPLOAD_TYPE = {
  map: 'albedo',
  normalMap: 'NormalMap',
  metalnessMap: 'metallic',
  roughnessMap: 'roughness',
  emissiveMap: 'emission',
  alphaMap: 'alpha',
  aoMap: 'ao',
  lightMap: 'light',
  specularMap: 'specular',
  bumpMap: 'bump',
  displacementMap: 'displacement',
  envMap: 'env',
};

const getSelectionId = (obj) =>
  obj ? (obj.uuid ?? obj._id ?? obj.id ?? obj.name ?? null) : null;

/** Material panel: preset controls + values synced from the selected mesh material. */
const MaterialComponentParent = React.memo(({ objectt }) => {
  const { selectedObject } = objectt;
  const materialMapFileInputRef = useRef(null);
  const emissionTextureFileInputRef = useRef(null);
  const fileTypeRef = useRef(null);
  const activeMapUploadKeyRef = useRef(null);

  const [materialMaps, setMaterialMaps] = useState([]);
  const isUpdatingRef = useRef(false);
  const lastAppliedRef = useRef(null);
  const selectionIdRef = useRef(null);
  const addDataRef = useRef(null);

  const selectionId = getSelectionId(selectedObject);

  const getMaterialSettings = useCallback((object) => {
    if (!object) return null;
    if (object?.type === 'Group' || object?.type === 'Scene') {
      if (object.userData?.material_settings) {
        return object.userData.material_settings;
      }
      let firstMeshSettings = null;
      object.traverse((child) => {
        if (
          child.isMesh &&
          child.userData?.material_settings &&
          !firstMeshSettings
        ) {
          firstMeshSettings = child.userData.material_settings;
        }
      });
      return firstMeshSettings;
    }
    return object?.userData?.material_settings;
  }, []);

  const [addData, setAddData] = useState({
    albedo: '',
    metalicType: false,
    emissionType: false,
    updatedonce: false,
    __selectionId: null,
    __isUserEdit: false,
    __lastChangedKey: null,
  });
  addDataRef.current = addData;

  const refreshMaterialMaps = useCallback(() => {
    if (!selectedObject) {
      setMaterialMaps([]);
      return;
    }
    setMaterialMaps(collectMaterialMapList(selectedObject));
  }, [selectedObject]);

  useEffect(() => {
    refreshMaterialMaps();
  }, [refreshMaterialMaps, selectionId]);

  const handleMapVisibilityToggle = useCallback(
    (mapKey, nextVisible) => {
      if (!selectedObject) return;
      setMaterialMapVisibility(selectedObject, mapKey, nextVisible);
      const mapVisibility = syncMapVisibilitySettings(selectedObject);
      const forceTransparent =
        mapKey === 'alphaMap' && nextVisible
          ? true
          : ensureTransparentForActiveAlphaMap(selectedObject);

      setAddData((prev) => ({
        ...prev,
        mapVisibility,
        ...(forceTransparent ? { transparency: true } : {}),
        __isUserEdit: true,
        __lastChangedKey: forceTransparent ? 'transparency' : 'mapVisibility',
      }));
      refreshMaterialMaps();
    },
    [selectedObject, refreshMaterialMaps]
  );

  useEffect(() => {
    if (!selectedObject || selectionId === null) {
      selectionIdRef.current = null;
      lastAppliedRef.current = null;
      return;
    }
    if (selectionIdRef.current === selectionId) return;
    selectionIdRef.current = selectionId;

    const settings = getMaterialSettings(selectedObject);
    let meshes;
    if (
      selectedObject?.type === 'Group' ||
      selectedObject?.type === 'Scene'
    ) {
      meshes = [];
      selectedObject.traverse((child) => {
        if (child.isMesh) meshes.push(child);
      });
      if (meshes.length === 0) return;
    }

    const next = buildMaterialPanelStateForSelection(
      selectedObject,
      settings,
      {
        selectionId,
        ...(meshes ? { meshes } : {}),
      }
    );

    lastAppliedRef.current = JSON.stringify(
      buildMaterialSettingsPayload(next)
    );
    setAddData(next);
  }, [selectionId, selectedObject, getMaterialSettings]);

  const HandleChange = useCallback((value, key) => {
    setAddData((prev) => ({
      ...prev,
      ...(key === 'opacity'
        ? { opacity: value, alpha: value }
        : { [key]: value }),
      __isUserEdit: true,
      __lastChangedKey: key,
      ...(key === 'emissionType' && value === true
        ? { emission: '#FFFFFF' }
        : key === 'emissionType' && value === false
          ? { emission: '', textureImage: '' }
          : {}),
      ...(key === 'metalicType' && value === false
        ? {
            metallic: {
              ...prev?.metallic,
              metalness: 0.5,
              roughness: 0.5,
              map: '',
            },
          }
        : {}),
      ...(key === 'materialType'
        ? {
            shader:
              {
                MeshStandardMaterial: 'Standard',
                MeshPhysicalMaterial: 'PBR',
                MeshBasicMaterial: 'Unlit',
              }[value] ?? prev.shader,
          }
        : {}),
    }));
  }, []);

  const applyPayload = useMemo(
    () =>
      addData.updatedonce ? buildMaterialSettingsPayload(addData) : null,
    [
      addData.updatedonce,
      addData.renderingMode,
      addData.materialType,
      addData.shader,
      addData.albedo,
      addData.alpha,
      addData.opacity,
      addData.side,
      addData.blending,
      addData.emission,
      addData.emissionType,
      addData.metallic?.metalness,
      addData.metallic?.roughness,
      addData.metallic?.map,
      addData.normal_map,
      addData.transparency,
      addData.textureImage,
      addData.maps,
      addData.mapVisibility,
      addData.aoMapIntensity,
      addData.metalicType,
      addData.mirror,
      addData.castShadow,
      addData.receiveShadow,
    ]
  );

  const handleShadowChange = useCallback(
    (key, value) => {
      if (!selectedObject) return;
      HandleChange(value, key);
      applyShadowSettingsToObject(selectedObject, {
        castShadow: key === 'castShadow' ? value : addDataRef.current?.castShadow,
        receiveShadow:
          key === 'receiveShadow' ? value : addDataRef.current?.receiveShadow,
      });
    },
    [selectedObject, HandleChange]
  );

  useEffect(() => {
    if (!selectedObject || !applyPayload || isUpdatingRef.current) return;
    if (addData.__selectionId !== selectionId) return;
    if (!addData.__isUserEdit) return;
    // Texture map uploads record their own MaterialTextureMapCommand step.
    if (addData.__lastChangedKey === 'maps') return;

    const payloadKey = JSON.stringify(applyPayload);
    if (lastAppliedRef.current === payloadKey) return;
    lastAppliedRef.current = payloadKey;
    isUpdatingRef.current = true;

    const obj = selectedObject;
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        try {
          if (!obj) return;
          const prevMaterialData = buildMaterialSettingsPayload(
            obj.userData.material_settings || {}
          );
          const newMaterialData = buildMaterialSettingsPayload(
            addDataRef.current,
            {
              includeMeta: true,
              changedKey: addDataRef.current.__lastChangedKey,
            }
          );
          undoRedoManager.executeCommand(
            new MaterialCommand(obj, prevMaterialData, newMaterialData)
          );
        } catch (error) {
          console.error('Error updating material:', error);
        } finally {
          isUpdatingRef.current = false;
        }
      });
    }, 150);

    return () => {
      clearTimeout(timeoutId);
      isUpdatingRef.current = false;
    };
  }, [selectedObject, applyPayload, selectionId, addData.__selectionId, addData.__isUserEdit]);

  const applyTextureToSelection = useCallback(
    (imageUrl, mapKey) => {
      if (!selectedObject || !mapKey) return;

      const uploadType = MAP_KEY_TO_UPLOAD_TYPE[mapKey] || 'albedo';
      const addDataForUpload = {
        ...addDataRef.current,
        __isUserEdit: true,
        __lastChangedKey: 'maps',
        normal_map:
          mapKey === 'normalMap' ? 'Map' : addDataRef.current?.normal_map,
      };

      // Capture map state before upload so undo restores previous textures.
      const prevMapEntries = captureObjectMapSlotStates(selectedObject, mapKey);

      // Update preview immediately so the thumbnail doesn't wait on TextureLoader.
      getMeshesFromObject(selectedObject).forEach((mesh) => {
        const materials = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];
        materials.forEach((mat) => {
          if (mat) setMapPreviewUrl(mat, mapKey, imageUrl);
        });
      });
      setMaterialMaps((prev) =>
        prev.map((item) =>
          item.key === mapKey
            ? {
                ...item,
                previewUrl: imageUrl,
                hasTexture: true,
                enabled: true,
              }
            : item
        )
      );

      updateObjeUrl({
        object: selectedObject,
        addData: addDataForUpload,
        url: imageUrl,
        type: uploadType,
      }).then(() => {
        setMaterialMapVisibility(selectedObject, mapKey, true);
        refreshMaterialMaps();
        const nextMapEntries = captureObjectMapSlotStates(
          selectedObject,
          mapKey
        );
        undoRedoManager.recordSnapshot(
          new MaterialTextureMapCommand(
            selectedObject,
            mapKey,
            prevMapEntries,
            nextMapEntries
          )
        );
      });

      setMaterialMapVisibility(selectedObject, mapKey, true);

      const mapVisibility = syncMapVisibilitySettings(selectedObject);
      // Only persist CDN/http URLs in maps — blob/data URLs are editor-only.
      const maps = { ...(addDataRef.current?.maps || {}) };
      if (isPublishableTextureUrl(imageUrl)) {
        maps[mapKey] = imageUrl;
      } else {
        delete maps[mapKey];
      }

      const patch = {
        maps,
        mapVisibility: { ...mapVisibility, [mapKey]: true },
        ...(mapKey === 'alphaMap' ? { transparency: true } : {}),
        ...(mapKey === 'metalnessMap'
          ? {
              metallic: {
                ...(addDataRef.current?.metallic || {}),
                map: isPublishableTextureUrl(imageUrl) ? imageUrl : '',
              },
            }
          : {}),
        ...(mapKey === 'normalMap' ? { normal_map: 'Map' } : {}),
        ...(mapKey === 'emissiveMap'
          ? {
              textureImage: isPublishableTextureUrl(imageUrl) ? imageUrl : '',
              emissionType: true,
            }
          : {}),
        __isUserEdit: true,
        __lastChangedKey: mapKey === 'alphaMap' ? 'transparency' : 'maps',
      };

      const newMaterialData = sanitizeMaterialPanelState({
        ...addDataRef.current,
        ...patch,
      });

      const applySettings = (target) => {
        if (!target) return;
        target.userData = target.userData || {};
        target.userData.material_settings = newMaterialData;
        target.userData.threeMaterialEdited = true;
      };

      selectedObject.traverse?.((child) => {
        if (child.isMesh) applySettings(child);
      });
      applySettings(selectedObject.isMesh ? selectedObject : null);
      if (!selectedObject.isMesh) {
        selectedObject.userData = selectedObject.userData || {};
        selectedObject.userData.material_settings = newMaterialData;
        selectedObject.userData.threeMaterialEdited = true;
      }

      setAddData((prev) => ({
        ...prev,
        ...patch,
        emissionType:
          mapKey === 'emissiveMap' ? true : prev.emissionType,
      }));
    },
    [selectedObject, refreshMaterialMaps]
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const mapKey = activeMapUploadKeyRef.current;
    if (!file || !selectedObject || !mapKey) return;

    // blob: URLs avoid multi-MB base64 data URLs that freeze publish / Redux.
    const objectUrl = URL.createObjectURL(file);
    applyTextureToSelection(objectUrl, mapKey);

    e.target.value = '';
    activeMapUploadKeyRef.current = null;
  };

  const openMapUpload = (mapKey) => {
    activeMapUploadKeyRef.current = mapKey;
    materialMapFileInputRef.current?.click();
  };

  if (!selectedObject) {
    return (
      <p className="text-xs text-gray-500 px-2 py-4">
        Select an asset to edit materials.
      </p>
    );
  }

  return (
    <div className="text-xs flex flex-col gap-1 py-2">
      {/* <GridDropdown
        label="Rendering Mode"
        value={addData.renderingMode || ''}
        onChange={(v) => HandleChange(v.target.value, 'renderingMode')}
        options={[
          { value: 'Opaque', label: 'Opaque' },
          { value: 'Cutout', label: 'Cutout' },
          { value: 'Fade', label: 'Fade' },
          { value: 'Wireframe', label: 'Wireframe' },
          { value: 'Original', label: 'Original' },
        ]}
      /> */}

      <GridDropdown
        label="Material Type"
        value={
          addData.materialType ||
          resolveMaterialType(null, addData.shader)
        }
        onChange={(v) => HandleChange(v.target.value, 'materialType')}
        options={MATERIAL_TYPE_OPTIONS}
      />

      <ColorInput
        label="Albedo (Base Color)"
        color={addData.albedo}
        setColor={(value) => {
          if (selectedObject) {
            applyAlbedoColorToObject(selectedObject, value);
          }
          HandleChange(value, 'albedo');
        }}
        bottomPanelSelector="#timeline-panel"
      />

      {materialMaps.map((mapItem) => (
        <React.Fragment key={mapItem.key}>
          <MaterialMapRow
            label={mapItem.label}
            previewUrl={mapItem.previewUrl}
            texture={mapItem.texture}
            checked={mapItem.enabled}
            disabled={!mapItem.hasTexture}
            onToggle={() =>
              handleMapVisibilityToggle(mapItem.key, !mapItem.enabled)
            }
            onThumbnailClick={() => openMapUpload(mapItem.key)}
          />
          {mapItem.key === 'aoMap' && mapItem.hasTexture && mapItem.enabled && (
            <EnhancedSlider
              toFix={2}
              label="AO Intensity"
              value={mapItem.aoMapIntensity ?? 1}
              onChange={(value) => {
                if (!selectedObject) return;
                selectedObject.traverse?.((child) => {
                  if (!child.isMesh) return;
                  const mats = Array.isArray(child.material)
                    ? child.material
                    : [child.material];
                  mats.forEach((mat) => {
                    if (mat) {
                      mat.aoMapIntensity = value;
                      mat.needsUpdate = true;
                    }
                  });
                });
                HandleChange(value, 'aoMapIntensity');
              }}
              min={0}
              max={2}
              step={0.01}
              unit=""
            />
          )}
        </React.Fragment>
      ))}

      <input
        ref={materialMapFileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <EnhancedSlider
        toFix={2}
        label="Metalness"
        value={addData.metallic?.metalness ?? 0.5}
        onChange={(value) =>
          setAddData((prev) => ({
            ...prev,
            metallic: { ...prev.metallic, metalness: value },
            __isUserEdit: true,
            __lastChangedKey: 'metallic',
          }))
        }
        min={0}
        max={1}
        step={0.01}
        unit=""
      />

      <EnhancedSlider
        toFix={2}
        label="Roughness"
        value={addData.metallic?.roughness ?? 0.5}
        onChange={(value) =>
          setAddData((prev) => ({
            ...prev,
            metallic: { ...prev.metallic, roughness: value },
            __isUserEdit: true,
            __lastChangedKey: 'metallic',
          }))
        }
        min={0}
        max={1}
        step={0.01}
        unit=""
      />

      <ToggleCheckbox
        label="Emission"
        material
        checked={addData?.emissionType || false}
        onToggle={() => HandleChange(!addData?.emissionType, 'emissionType')}
      />

      {addData?.emissionType && (
        <>
          <ColorInput
            label="Color"
            color={addData.emission}
            setColor={(value) => HandleChange(value, 'emission')}
            bottomPanelSelector="#timeline-panel"
          />
          {/* <GridUploadInput
            label="Texture"
            onChange={handleFileChange}
            fileInputRef={emissionTextureFileInputRef}
            fileTypeRef={fileTypeRef}
          /> */}
        </>
      )}

      <GridDropdown
        label="Side"
        value={addData.side || 'Front'}
        onChange={(v) => HandleChange(v.target.value, 'side')}
        options={MATERIAL_SIDE_OPTIONS}
      />

      <EnhancedSlider
        toFix={2}
        label="Opacity"
        value={addData.opacity ?? addData.alpha ?? 1}
        onChange={(value) => HandleChange(value, 'opacity')}
        min={0}
        max={1}
        step={0.01}
        unit=""
      />

      <ToggleCheckbox
        label="Transparent"
        material
        checked={!!addData.transparency}
        onToggle={() => HandleChange(!addData.transparency, 'transparency')}
      />

      <MaterialShadowRow
        castChecked={!!addData.castShadow}
        receiveChecked={!!addData.receiveShadow}
        onCastToggle={() =>
          handleShadowChange('castShadow', !addData.castShadow)
        }
        onReceiveToggle={() =>
          handleShadowChange('receiveShadow', !addData.receiveShadow)
        }
      />

      {shouldShowFullMaterialEditor(selectedObject) && (
        <MaterialPresetFields
          selectedObject={selectedObject}
          onPanelSync={(patch) =>
            setAddData((prev) => ({
              ...prev,
              ...patch,
              __isUserEdit: false,
            }))
          }
        />
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  const prevId = getSelectionId(prevProps.objectt?.selectedObject);
  const nextId = getSelectionId(nextProps.objectt?.selectedObject);
  return prevId === nextId;
});

export default MaterialComponentParent;
